import React, { useRef } from 'react';
import { DAY_WIDTH, WEEK_WIDTH } from '../WallPlannerStructuralComponents';
import styled, { css } from "styled-components";
import { DATE_FORMAT, daysBetween } from '@shared/utils/DateUtils';
import Colours from '@shared/ui/atoms/Colours';
import { BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { isBusy, isBusyInWork, isWorkingFromOffice } from '@shared/utils/Movement';
import { UNKNOWN_OPTION } from '@features/whereabouts/services/WhereaboutOptions';
import moment, { Moment } from "moment";
import { Period } from '@features/whereabouts/models/movements.models';
import { WhereaboutsIconOutline } from '@shared/ui/atoms/WhereaboutsIconOutline';
import { IconPack } from '@shared/models/company.models';
import { useDispatch } from "react-redux";
import { setIsVisible, setPositionAndMakeVisible } from "../data/WallPlannerHoverBox.duck";
import { UsersWorkBlock } from '@features/wall-planner/wall-planner.models';
import { Birthday } from '@shared/models/user.models';

interface Props {
  whereabouts: UsersWorkBlock;
  firstDay: Moment;
  sideOfDay: Period;
  iconPack: IconPack;
  birthday?: Birthday;
  style?: any;
}

// Splits a run of days, counted from the first day on the calendar, at every week boundary.
function weekRuns(start: number, days: number) {
  const runs: {start: number, days: number}[] = [];
  let dayOffset = start;
  let daysLeft = days;

  while (daysLeft > 0) {
    const dayOfWeek = ((dayOffset % 7) + 7) % 7;
    const runDays = Math.min(daysLeft, 7 - dayOfWeek);
    runs.push({start: dayOffset, days: runDays});
    dayOffset += runDays;
    daysLeft -= runDays;
  }

  return runs;
}

export function ActivityBlockV2(props: Props) {
  const {whereabouts, firstDay, style, sideOfDay} = props;
  const option = sideOfDay === Period.AM ? whereabouts.amOption : whereabouts.pmOption;

  if (!option || (option.key || UNKNOWN_OPTION.key) === UNKNOWN_OPTION.key) {
    // One fill per week: an unconfirmed run reads as a gap in each week rather than a bar sliding
    // across the whole month.
    const runs = weekRuns(daysBetween(whereabouts.date, firstDay), daysBetween(whereabouts.toDate, whereabouts.fromDate) + 1);
    return (
      <>
        {runs.map((run, key) => (
          <PendingWorkingFill key={key}
                              start={run.start}
                              days={run.days}
                              title="Whereabouts waiting to be confirmed" style={style ?? {}} />
        ))}
      </>
    )
  } else if (isBusy(option)) {
    return (
      <HolidayActivity start={daysBetween(whereabouts.date, firstDay)}
                       days={daysBetween(whereabouts.toDate, whereabouts.fromDate) + 1}
                       iconPack={props.iconPack}
                       activity={whereabouts}
                       sideOfDay={sideOfDay} style={style ?? {}} />
    )
  } else if (isBusyInWork(option)) {
    return (
      <BusyWorkingCell start={daysBetween(whereabouts.date, firstDay)}
                       days={daysBetween(whereabouts.toDate, whereabouts.fromDate) + 1}
                       iconPack={props.iconPack}
                       sideOfDay={sideOfDay}
                       activity={whereabouts}
                       style={style ?? {}} />
    )
  }  else if (isWorkingFromOffice(option)) {
    return (
      <WorkingLightGreen start={daysBetween(whereabouts.date, firstDay)}
                         iconPack={props.iconPack}
                         days={daysBetween(whereabouts.toDate, whereabouts.fromDate) + 1}
                         sideOfDay={sideOfDay}
                         activity={whereabouts}
                         style={style ?? {}} />
    )
  } else {
    return (
      <WorkDarkGreen start={daysBetween(whereabouts.date, firstDay)}
                     days={daysBetween(whereabouts.toDate, whereabouts.fromDate) + 1}
                     sideOfDay={sideOfDay}
                     iconPack={props.iconPack}
                     activity={whereabouts}
                     style={style ?? {}} />
    )
  }
}

interface WorkingFillProps {
  activity: UsersWorkBlock;
  sideOfDay: Period;
  start: number;
  days: number;
  style?: any;
  hovering?: boolean;
  iconPack: IconPack;
}

// A block covers a run of conjoined days, so work out which day is under the cursor. The hover box
// shows the week that day belongs to, which is not always the week the block started in.
function useHoverBoxTriggers(activity: any, days: number) {
  const dispatch = useDispatch();
  const shownDate = useRef<string | undefined>(undefined);

  const show = (e: any) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetTop = window.pageYOffset || document.documentElement.scrollTop;
    const offsetLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? rect.left;
    const dayOffset = Math.min(Math.max(Math.floor((clientX - rect.left) / DAY_WIDTH), 0), Math.max(days - 1, 0));
    const hoveredDay = moment(activity.date).add(dayOffset, 'days');
    const hoveredDate = hoveredDay.format(DATE_FORMAT);

    if (hoveredDate === shownDate.current) return;
    shownDate.current = hoveredDate;

    // Days are a uniform width, so the week containing the hovered day can be measured off the fill.
    // The box hangs off the end of this run of whereabouts, clipped to the week it is describing so
    // a run carrying on into the next week does not drag the box along with it.
    const weekLeft = rect.left + (dayOffset - (hoveredDay.isoWeekday() - 1)) * DAY_WIDTH;
    const weekRight = weekLeft + WEEK_WIDTH;

    dispatch(setPositionAndMakeVisible({
      anchor: {
        top: rect.top + offsetTop,
        bottom: rect.bottom + offsetTop,
        left: Math.max(rect.left, weekLeft) + offsetLeft,
        right: Math.min(rect.right, weekRight) + offsetLeft,
      },
      userId: activity.userId,
      fromDate: hoveredDate,
    }));
  };

  const hide = () => {
    shownDate.current = undefined;
    dispatch(setIsVisible(false));
  };

  return {
    onMouseOver: show,
    onMouseMove: show,
    onMouseLeave: hide,
    onTouchStart: show,
    onTouchEnd: hide,
    onTouchCancel: hide,
  };
}

function WorkDarkGreen(props: WorkingFillProps) {
  const hoverTriggers = useHoverBoxTriggers(props.activity, props.days);
  return <ConfirmedWorkingFill start={props.start} days={props.days}
                               style={props?.style ?? {}}
                               {...hoverTriggers}>
    <WorkingCell {...props} />
  </ConfirmedWorkingFill>
}

// The option item label (picked option or free text) shown along a multi-day bar. Only returned
// when every day in the block carries the same label, so the bar never claims a selection that
// only applies to part of the run; mixed runs fall back to icons, detail stays on hover.
function blockOptionItemLabel(activity: UsersWorkBlock, sideOfDay: Period): string | undefined {
  const days: any[] = activity.collection ?? [activity];
  const labels = days.map(day => sideOfDay === Period.AM ? day.amOptionItemLabel : day.pmOptionItemLabel);
  const first = labels[0];
  if (!first) return undefined;
  return labels.every(label => label === first) ? first : undefined;
}

function HolidayActivity(props: WorkingFillProps) {
  const hoverTriggers = useHoverBoxTriggers(props.activity, props.days);

  const optionForSideOfDay = props.sideOfDay === Period.AM ? props.activity.amOption : props.activity.pmOption
  const optionItemLabel = props.days > 1 ? blockOptionItemLabel(props.activity, props.sideOfDay) : undefined;

  return <HolidayActivityFill {...props}
                              {...hoverTriggers}>
    {optionItemLabel ? (
      <IconWithText>
        <WhereaboutsIconOutline label={optionForSideOfDay?.label ?? ''}
                                image={optionForSideOfDay?.image ?? ''}
                                colour={optionForSideOfDay?.colour ?? ''} />
        <OptionItemText title={optionItemLabel}>{optionItemLabel}</OptionItemText>
      </IconWithText>
    ) : (
      <WhereaboutsIconOutline label={optionForSideOfDay?.label ?? ''}
                              image={optionForSideOfDay?.image ?? ''}
                              colour={optionForSideOfDay?.colour ?? ''} />
    )}
  </HolidayActivityFill>
}

function WorkingLightGreen(props: WorkingFillProps) {
  const hoverTriggers = useHoverBoxTriggers(props.activity, props.days);
  return <ConfirmedWorkingFromHomeFill start={props.start} days={props.days}
                                       style={props?.style ?? {}}
                                       {...hoverTriggers}>
    <WorkingCell {...props} />
  </ConfirmedWorkingFromHomeFill>
}

function BusyWorkingCell(props: WorkingFillProps) {
  const hoverTriggers = useHoverBoxTriggers(props.activity, props.days);
  return <BusyInWorkFill start={props.start} days={props.days}
                         style={props?.style ?? {}}
                         {...hoverTriggers}>
    <WorkingCell {...props} />
  </BusyInWorkFill>
}

function WorkingCell(props: WorkingFillProps) {
  const {activity, sideOfDay} = props;
  const optionForSideOfDay = sideOfDay === Period.AM ? activity.amOption : activity.pmOption
  const optionItemLabel = activity.collection.length > 1 ? blockOptionItemLabel(activity, sideOfDay) : undefined;

  if (optionItemLabel) {
    return <IconWithText>
      <WhereaboutsIconOutline label={optionForSideOfDay?.label ?? ''}
                              image={optionForSideOfDay?.image ?? ''}
                              colour={optionForSideOfDay?.colour ?? ''} />
      <OptionItemText title={optionItemLabel}>{optionItemLabel}</OptionItemText>
    </IconWithText>
  }

  return <>
    {activity.collection.map((m: any, k: number) => <WhereaboutsIconOutline key={k} label={optionForSideOfDay?.label ?? ''}
                                                                            image={optionForSideOfDay?.image ?? ''}
                                                                            colour={optionForSideOfDay?.colour ?? ''} />)}
  </>
}

const OptionItemText = styled(BodyVerySmall)`
  flex: 1;
  min-width: 0;
  margin: 0 8px 0 0;
  font-size: 15px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

// The icon sets its own 16px inline right margin; tighten it when text follows.
const IconWithText = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  .whereabouts__outlineIcon {
    margin-right: 8px !important;
  }
`

const ActivityFill = styled.div<any>`
  position: absolute;
  height: 32px;
  align-self: center;
  border-radius: 4px;
  transition: background .25s;
  display: flex;
  align-items: center;
  padding-left: 4px;
  user-select: none;
  overflow: hidden;
  ${props => css`left: ${props.start ? ((props.start * (DAY_WIDTH) + 2) + WEEK_WIDTH + (props.startsWithHalfDay ? DAY_WIDTH * 0.5 : 0)) + 'px' : (WEEK_WIDTH + 2) + 'px'};`};
  ${props => css`width: ${props.days ? (props.days * (DAY_WIDTH) - 6 - ((props.startsWithHalfDay || props.endsWithHalfDay) ? DAY_WIDTH * 0.5 : 0)) + 'px' : 0};`};
  .whereabouts__outlineIcon {
    max-height: 80% !important;
  }
`

const ConfirmedWorkingFill = styled(ActivityFill)<any>`
  background: #BDFAC8;
  border: 1px solid #4DCD67;
`

const ConfirmedWorkingFromHomeFill = styled(ActivityFill)<any>`
  background: #E3FFAB;
  border: 1px solid #4DCD67;
`

const PendingWorkingFill = styled(ActivityFill)<any>`
  background: #F9FFDD;
  border: 1px solid #BCEB29;
`

const BusyInWorkFill = styled(ActivityFill)<any>`
  background: #FFF6A1;
  border: 1px solid #FFD027;
`

const HolidayActivityFill = styled(ActivityFill)<any>`
  background: ${props => !!props.fill ? props.fill : Colours.veryLightGrey};
  color: ${props => !!props.textColour ? props.textColour : Colours.black};
  border: 1px solid ${props => !!props.outline ? props.outline : '#A6B4B4'};
  transition: border .1s ease-in-out;
  cursor: pointer;
  p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .icon {
    color: ${props => !!props.textColour ? props.textColour : Colours.black} !important;
  }
  &:hover, &:active {
    border: 1px solid ${Colours.blue};
  }
`
